import { reactive, watch } from "vue";
import { Servis } from "@/services/Servis";

// BSL-D3-3 (B-28): bekleyen adım sekme belleğinde (sessionStorage) saklanır; sayfa yenilenince
// kaldığı yerden döner. 30 dakikadan eski kayıt atılır. Sekme kapanınca kayıt gider.
const BEKLEYEN_ANAHTAR = "bsl-bekleyen";
const BEKLEYEN_SURE_MS = 30 * 60 * 1000;

function bekleyenOku() {
  try {
    const ham = sessionStorage.getItem(BEKLEYEN_ANAHTAR);
    if (!ham) return null;
    const k = JSON.parse(ham);
    if (!k || !k.kod || !k.zaman || Date.now() - k.zaman > BEKLEYEN_SURE_MS) {
      sessionStorage.removeItem(BEKLEYEN_ANAHTAR);
      return null;
    }
    return { kod: k.kod, hat: k.hat || "ANA" };
  } catch (e) {
    return null;
  }
}

function bekleyenYaz(b) {
  try {
    if (b) sessionStorage.setItem(BEKLEYEN_ANAHTAR, JSON.stringify({ kod: b.kod, hat: b.hat, zaman: Date.now() }));
    else sessionStorage.removeItem(BEKLEYEN_ANAHTAR);
  } catch (e) { /* sessiz */ }
}

// BSL — Başlarken (Dokumanlar/Arayuz/Baslarken/BASLARKEN-PLANI.md §8). Tek reaktif durum:
// AdminLayout (kurulum penceresi + hap + tur balonu), ? menüsü ve boş ekranlar okur.
// BSL-PENCERE (2026-10-01): kart ve ayrı karşılama yerine tek pencere (KurulumPenceresi, hatlar.js).
// Tamamlanma BE'de veriden hesaplanır; burada yalnız son cevap tutulur.
export const bsl = reactive({
  durum: null,            // GetBaslarkenDurum cevabı ({ Gizli } ya da tam durum)
  pencereAcik: false,     // BSL-PENCERE: kurulum penceresi
  pencereHat: "ANA",      // "ANA" ya da modül hattı (TKL/DST/BLG) — hatlar.js
  pencereEkran: null,     // açılırken istenen ekran: "karsilama" | "sektor" | "basari" | null (sıradaki adım)
  bekleyenAdim: bekleyenOku(), // { kod, hat } — «Göster» adımı sayfada bitince pencere başarı ekranıyla döner (B-28: sekme belleğinden)
  ornekMaddeKod: null,    // «Örnekle başla» önizlemesi açık mı (madde kodu)
  turIstegi: null,        // { anahtar, yol, kisa } — bellekte, URL'de iz yok (§8); kisa: pencereden, ilk + son adım
  ornekSayac: 0,          // örnek eklenince artar; açık listeler (Ürün Listesi) yenilenir
  pencereIstegi: null,    // madde bir sayfa penceresini açtırır (ör. MUS-YUKLE → Toplu Yükle); sayfa okuyup sıfırlar
  sekmeIstegi: null,      // BSL-RDV: tur sekmeli sayfada kendi sekmesini açtırır (Randevu Ayarları); sayfa okuyup sıfırlar
  turAktif: false,        // BSL-PENCERE: tur balonu açıkken köşe hapı gizlenir
  turHazirlik: false,     // BSL-D2 (B-12): tur hedef arıyor (balon henüz açılmadı); pencere dönüşü bekler
  sonTur: null,           // BSL-TKL: { anahtar, zaman } — balon açılınca yazılır; çok adımlı formlar sıradaki turu buna göre ister
});

// B-28: değişince yazılır, null olunca silinir (yüklemedeki değer yeniden yazılmaz; zamanı korunur).
watch(() => bsl.bekleyenAdim, bekleyenYaz, { flush: "sync" });

let _istek = null;

// zorla=false: aynı sayfa yüklemesinde tek istek (lisans.js deseni). Pencere/boş ekran zorla tazeler.
export function durumYukle(zorla = false) {
  if (_istek && !zorla) return _istek;
  _istek = Servis("GET", "GetBaslarkenDurum", "")
    .then((res) => {
      bsl.durum = res.status === 200 && res.data ? res.data : { Gizli: true };
      return bsl.durum;
    })
    .catch(() => {
      _istek = null;
      return bsl.durum;
    });
  return _istek;
}

// Login/çıkış: önceki kullanıcının listesi kalmasın.
export function durumSifirla() {
  _istek = null;
  bsl.durum = null;
  bsl.pencereAcik = false;
  bsl.bekleyenAdim = null;
  bekleyenYaz(null); // B-28: kayıt başka kullanıcıya geçmez (değer zaten null olsa da sil)
  bsl.turIstegi = null;
}

export function gorunurMu() {
  return !!(bsl.durum && !bsl.durum.Gizli);
}

export function madde(kod) {
  const d = bsl.durum;
  if (!d || d.Gizli || !Array.isArray(d.Maddeler)) return null;
  return d.Maddeler.find((m) => m.Kod === kod) || null;
}

export function ornekVar(kod) {
  const m = madde(kod);
  return !!(m && m.OrnekVar && !m.Tamam);
}

export function kullaniciIslem(Islem, MaddeKod) {
  return Servis("POST", "SetBaslarkenKullanici", { Islem, MaddeKod: MaddeKod || null });
}

export function cevapKaydet(Sektor, MusteriListesi) {
  return Servis("POST", "SetBaslarkenCevap", { Sektor: Sektor || null, MusteriListesi: MusteriListesi || null });
}

// Kullanıcı menüsü «Başlarken», ? menüsü «Kurulum adımları», hap. Kapatılmışsa (KartKapali) açar.
export function pencereAc(hat = "ANA", ekran = null) {
  bsl.pencereHat = hat || "ANA";
  bsl.pencereEkran = ekran;
  bsl.pencereAcik = true;
  if (bsl.durum && bsl.durum.KartKapali) {
    kullaniciIslem("Ac").then(() => durumYukle(true)).catch(() => {});
  }
}

export function turIste(anahtar, yol, kisa = false) {
  bsl.turIstegi = { anahtar, yol: yol || null, kisa: !!kisa };
}

export function pencereIste(anahtar) {
  bsl.pencereIstegi = anahtar;
}

export function ornekAc(kod) {
  bsl.ornekMaddeKod = kod;
}
